import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Crosshair, Flame, Trash2, Plus, TrendingDown,
  Award, BarChart3, Lock, Activity, Download, FileSpreadsheet,
} from 'lucide-react';
import clsx from 'clsx';
import { useRange } from './useRange';
import { useActiveSession } from './useActiveSession';
import { diagnose } from './diagnose';
import { GROUP_PATTERN_LABELS, ACHIEVEMENTS, type GroupPattern, type ShotSession } from './types';
import { useSubscription, openUpgrade } from '@/hooks/useSubscription';
import { ActiveSession } from './components/ActiveSession';
import { CompareView } from './components/CompareView';
import { DistancePicker } from './components/DistancePicker';
import { computeShotAggregates } from './aggregates';
import { buildSessionsCsv, buildShotsCsv, downloadCsv, isoToFilenameDate } from './csv';
import { buildSyncPayload, getOrCreateClientId, syncToServer } from './sync';
import { useRangePrefs } from './usePrefs';
import type { UseSetups } from '@/hooks/useSetups';
import type { SessionSetupSnapshot } from './v2-schema';
import { ShareButton } from '@/share/components/ShareButton';
import { buildSessionSnapshot } from '@/share/snapshotBuilders';
import { SignInCTA } from '@/auth/SignInCTA';
import { useCloudSync } from '@/sync/useCloudSync';
import { CloudOff, CloudCog, Loader2 } from 'lucide-react';
import { AggregateContributionCard } from './components/AggregateContributionCard';
import { contributeSession } from '@/sync/aggregateContribution';
type GP = GroupPattern;
type RangeSubTab = 'sessions' | 'compare';

/**
 * Range tab — practice session logger + auto-diagnose + PBs + streaks.
 *
 * Layout:
 *   1. Stats strip: total sessions / arrows / current streak / sessions this week
 *   2. "Log a session" button + the most-recent session's diagnosis card
 *   3. Personal bests grid (one card per logged distance)
 *   4. History table (collapsible, last 20 sessions)
 *   5. Achievements grid (earned + still-locked)
 *
 * Loop:
 *   - User shoots, opens app, logs session in 30s
 *   - Sees a specific tuning recommendation deep-linking into the Tune wizard
 *   - PB update if applicable → celebration
 *   - Streak ticks → habit reinforcement
 *   - Achievement unlock → dopamine
 */
interface RangeViewProps {
  setups?: UseSetups;
}

export function RangeView({ setups }: RangeViewProps = {}) {
  const range = useRange();
  const activeSessionHook = useActiveSession();
  const subscription = useSubscription();
  const prefs = useRangePrefs();
  const [logOpen, setLogOpen] = useState(false);
  const [beginOpen, setBeginOpen] = useState(false);
  const [subTab, setSubTab] = useState<RangeSubTab>('sessions');

  // v136.0 — cloud sync wired to the local session store. Anonymous users
  // see no chrome; signed-in users get pull-on-mount, push-on-save, and the
  // one-time backfill prompt.
  const cloud = useCloudSync({
    getLocalSessions: () => range.state.sessions,
    replaceLocalSessions: range.replaceSessions,
  });

  // Snapshot the live setup at the moment the user starts a session — so a
  // later setup change doesn't retroactively alter the session record.
  const snapshotSetup = (): SessionSetupSnapshot | undefined => {
    const s = setups?.activeSetup;
    const a = setups?.activeArrow;
    if (!s || !a) return undefined;
    return {
      setupId: s.id,
      setupName: s.name,
      bow: s.bow,
      sightRadius: s.sightRadius,
      peepHeight: s.peepHeight,
      sightedInYards: s.sightedInYards,
      arrow: a,
    };
  };

  // Commit the active session to the permanent log + fire sync stub.
  const commitActive = () => {
    const A = activeSessionHook.active;
    if (!A) return;
    if (A.shots.length === 0) {
      // Nothing to commit — just clear.
      activeSessionHook.end();
      return;
    }
    const aggs = computeShotAggregates(A.shots);
    const distances = Array.from(new Set(A.shots.map((s) => s.distanceYd))).sort((a, b) => a - b);
    // Aggregate-mode fields: best distance bucket as "the" session distance,
    // group radius doubled as a stand-in for outer-to-outer diameter.
    const primaryDistance = distances[Math.floor(distances.length / 2)] ?? A.shots[0].distanceYd;
    const groupSizeIn = aggs ? aggs.group95RadiusIn * 2 : 0;
    const pattern: GP = derivePattern(aggs);

    const session = range.logSession({
      setupId: A.setupSnapshot?.setupId,
      distanceYd: primaryDistance,
      arrowCount: A.shots.length,
      groupSizeIn,
      pattern,
      loggedAt: A.startedAt,
      richShots: A.shots,
      conditions: A.conditions,
      setupSnapshot: A.setupSnapshot,
      location: A.location,
      endedAt: new Date().toISOString(),
    });

    // Fire sync stub — non-blocking. v130 legacy aggregate sync.
    void syncToServer(buildSyncPayload(session, getOrCreateClientId()));
    // v136.0 — mirror to cloud (best-effort, no-op if anonymous).
    void cloud.mirror(session);
    // v138 — opt-in aggregate contribution (Worker checks the flag; non-opted users get 403, ignored).
    void contributeSession(session);

    activeSessionHook.end();
  };

  const exportSessionsCsv = () => {
    const filename = `sparrow-sessions-${isoToFilenameDate()}.csv`;
    downloadCsv(filename, buildSessionsCsv(range.state.sessions));
  };
  const exportShotsCsv = () => {
    const filename = `sparrow-shots-${isoToFilenameDate()}.csv`;
    downloadCsv(filename, buildShotsCsv(range.state.sessions));
  };

  const mostRecent = range.state.sessions[0];
  const recs = useMemo(
    () => mostRecent ? diagnose(mostRecent, range.state.sessions) : [],
    [mostRecent, range.state.sessions],
  );

  // Diagnose engine = Hunter+ only. Free users see the pattern was identified
  // but the WHY/FIX + deep-link into Tune is locked. Logging + PBs + streaks +
  // achievements stay Free (the daily-use gamification hook).
  const canSeeDiagnose = subscription.isHunterOrAbove;

  // PBs sorted by distance ascending
  const pbs = useMemo(
    () => Object.values(range.state.personalBests).sort((a, b) => a.distanceYd - b.distanceYd),
    [range.state.personalBests],
  );

  // If a live session is active, the entire view becomes the capture UI.
  // Quick-log + history + PBs are hidden until the session ends.
  if (activeSessionHook.active) {
    return (
      <ActiveSession
        session={activeSessionHook.active}
        onAddShot={(s) => activeSessionHook.addShot(s)}
        onRemoveShot={(id) => activeSessionHook.removeShot(id)}
        onEnd={commitActive}
        onAbandon={() => {
          if (confirm('Abandon this session? Unsaved shots will be discarded.')) {
            activeSessionHook.end();
          }
        }}
        captureMode={prefs.captureMode}
        onCaptureModeChange={prefs.setCaptureMode}
        unit={prefs.unit}
        onUnitChange={prefs.setUnit}
        onStartNewSet={activeSessionHook.startNewSet}
        onPatchCurrentSet={activeSessionHook.patchCurrentSet}
        onSetPhoto={activeSessionHook.setSetPhoto}
      />
    );
  }

  return (
    <div className="space-y-6 max-w-[1200px] mx-auto">
      {/* ───── HEADER ───── */}
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <Crosshair className="text-ember-500" size={22} />
            Range Log
          </h1>
          <p className="text-sm text-zinc-400 mt-0.5">
            Log every shot. Watch the trend. Get a specific fix for what's off.
          </p>
          {/* v136.0 — Sign-In CTA local to Range Log. Anonymous users see
              an ember Sign-in prompt; signed-in users see a cyan chip. */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <SignInCTA />
            {cloud.state === 'syncing' && (
              <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                <Loader2 size={11} className="animate-spin" /> Syncing…
              </span>
            )}
            {cloud.state === 'error' && cloud.errorMessage && (
              <span className="inline-flex items-center gap-1 text-[11px] text-rose-400">
                <CloudOff size={11} /> {cloud.errorMessage}
              </span>
            )}
            {cloud.state === 'idle' && cloud.lastSyncAt && (
              <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                <CloudCog size={11} /> Synced {formatRelative(new Date(cloud.lastSyncAt).toISOString())}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="md"
            leftIcon={<Activity size={16} />}
            onClick={() => setBeginOpen(true)}
          >
            Start live session
          </Button>
          <Button
            size="md"
            variant="secondary"
            leftIcon={<Plus size={14} />}
            onClick={() => setLogOpen(true)}
          >
            Quick log
          </Button>
        </div>
      </div>

      {/* v138 — Aggregate contribution opt-in card. Only meaningful when
          signed in; renders an informational state otherwise. */}
      <AggregateContributionCard />

      {/* v136.0 — Backfill prompt. One-time on first sign-in when local has
          sessions not yet in cloud. */}
      {cloud.backfillPending && (
        <div className="border border-cyan-700/40 bg-cyan-950/20 rounded-md p-4 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-[14rem]">
            <p className="text-sm text-cyan-200 font-medium">
              Sync your existing {cloud.backfillCount} local session{cloud.backfillCount === 1 ? '' : 's'} to your account?
            </p>
            <p className="text-xs text-zinc-400 mt-1">
              Photos stay on this device — only the shot data syncs.
              You can change this any time from <a href="/pages/privacy" target="_top" className="text-cyan-300 hover:underline">Privacy</a>.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void cloud.runBackfill()}
              className="px-3 py-2 bg-cyan-500 text-zinc-950 rounded text-sm font-semibold hover:bg-cyan-400"
            >
              Sync them
            </button>
            <button
              onClick={cloud.declineBackfill}
              className="px-3 py-2 border border-zinc-700 text-zinc-300 rounded text-sm hover:bg-zinc-900"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {/* ───── SUB-TAB SWITCH ───── */}
      <div className="inline-flex border border-zinc-800 rounded overflow-hidden">
        <button
          onClick={() => setSubTab('sessions')}
          className={clsx('px-3 py-2 min-h-[44px] text-[12px] font-mono uppercase tracking-wider touch-manipulation',
            subTab === 'sessions' ? 'bg-ember-500/20 text-ember-300' : 'text-zinc-400 hover:bg-zinc-900')}
        >Sessions</button>
        <button
          onClick={() => setSubTab('compare')}
          className={clsx('px-3 py-2 min-h-[44px] text-[12px] font-mono uppercase tracking-wider touch-manipulation',
            subTab === 'compare' ? 'bg-ember-500/20 text-ember-300' : 'text-zinc-400 hover:bg-zinc-900')}
        >Compare</button>
      </div>

      {subTab === 'compare' && (
        <CompareView sessions={range.state.sessions} />
      )}

      {subTab === 'sessions' && (
      <>
      {/* ───── STATS STRIP ───── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Sessions" value={range.stats.totalSessions} sub="logged" />
        <StatCard label="Arrows" value={range.stats.totalArrows} sub="cumulative" />
        <StatCard
          label="Streak"
          value={range.stats.currentStreakDays}
          sub={range.stats.currentStreakDays === 1 ? 'day' : 'days in a row'}
          accent="ember"
          icon={range.stats.currentStreakDays >= 3 ? <Flame size={14} className="text-ember-500" /> : undefined}
        />
        <StatCard
          label="This week"
          value={range.stats.sessionsThisWeek}
          sub={range.stats.sessionsThisWeek === 1 ? 'session' : 'sessions'}
        />
      </div>

      {/* ───── LATEST DIAGNOSIS ───── */}
      {mostRecent && recs.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={14} className="text-ember-500" />
            <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">
              Last session — {formatRelative(mostRecent.loggedAt)} · {mostRecent.distanceYd}yd · {mostRecent.groupSizeIn.toFixed(1)}" group
            </h2>
          </div>
          {canSeeDiagnose
            ? recs.map((rec, i) => <DiagnoseCard key={i} rec={rec} />)
            : <LockedDiagnoseTeaser recs={recs} />}
        </div>
      )}

      {/* ───── PERSONAL BESTS ───── */}
      {pbs.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Award size={14} className="text-amber-400" />
            <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">
              Personal Bests
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {pbs.map((pb) => (
              <Card key={pb.distanceYd} padding="md" className="border-amber-700/30 bg-amber-950/10">
                <p className="text-[10px] font-mono uppercase tracking-wider text-amber-400 mb-1">
                  {pb.distanceYd} yd
                </p>
                <p className="text-2xl font-bold text-zinc-100">
                  {pb.groupSizeIn.toFixed(1)}<span className="text-sm text-zinc-500 ml-1">in</span>
                </p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  {pb.arrowCount} arrows · {formatRelative(pb.achievedAt)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ───── EMPTY STATE ───── */}
      {range.state.sessions.length === 0 && (
        <EmptyState
          icon={<Crosshair size={28} />}
          title="No sessions logged yet"
          description="Log your first range session and you'll get a specific tuning recommendation for what to work on next. Free, takes 30 seconds."
          action={<Button size="md" leftIcon={<Plus size={16} />} onClick={() => setLogOpen(true)}>Log your first session</Button>}
        />
      )}

      {/* ───── HISTORY TABLE ───── */}
      {range.state.sessions.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">
            Recent sessions
          </h2>
          <Card padding="md">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-right py-2 px-2">Dist</th>
                    <th className="text-right py-2 px-2">Arrows</th>
                    <th className="text-right py-2 px-2">Group</th>
                    <th className="text-left py-2 px-2">Pattern</th>
                    <th className="text-right py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {range.state.sessions.slice(0, 20).map((s) => (
                    <tr key={s.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/40">
                      <td className="py-2 px-2 text-zinc-300">{formatDate(s.loggedAt)}</td>
                      <td className="py-2 px-2 text-zinc-300 text-right">{s.distanceYd} yd</td>
                      <td className="py-2 px-2 text-zinc-400 text-right">{s.arrowCount}</td>
                      <td className="py-2 px-2 text-zinc-100 text-right font-semibold">
                        {s.groupSizeIn.toFixed(1)}"
                      </td>
                      <td className="py-2 px-2 text-zinc-400 text-[12px]">
                        {GROUP_PATTERN_LABELS[s.pattern]}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {/* v135 — Per-row share. Lazy snapshot built on click. */}
                        <SessionRowActions
                          session={s}
                          onDelete={() => {
                            if (confirm(`Delete this ${s.distanceYd}yd session?`)) {
                              range.deleteSession(s.id);
                              // v136.0 — mirror delete to cloud (best-effort, no-op anon).
                              void cloud.mirrorDelete(s.id);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {range.state.sessions.length > 20 && (
              <p className="text-[11px] text-zinc-500 text-center mt-3">
                Showing 20 of {range.state.sessions.length} sessions
              </p>
            )}
          </Card>
        </div>
      )}

      {/* ───── ACHIEVEMENTS ───── */}
      <div className="space-y-2">
        <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">
          Achievements ({range.stats.achievements.length}/{ACHIEVEMENTS.length})
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {ACHIEVEMENTS.map((a) => {
            const earned = range.stats.achievements.includes(a.id);
            const tierColor = {
              bronze: earned ? 'border-amber-700/60 bg-amber-950/20 text-amber-300' : 'border-zinc-800 bg-zinc-950/40 text-zinc-600',
              silver: earned ? 'border-zinc-400/60 bg-zinc-700/20 text-zinc-200' : 'border-zinc-800 bg-zinc-950/40 text-zinc-600',
              gold:   earned ? 'border-yellow-500/60 bg-yellow-950/20 text-yellow-300' : 'border-zinc-800 bg-zinc-950/40 text-zinc-600',
            }[a.tier];
            return (
              <div key={a.id} className={clsx('rounded-md border p-3', tierColor)}>
                <div className="flex items-baseline gap-1.5 mb-1">
                  <Award size={12} className={earned ? '' : 'opacity-30'} />
                  <p className="text-[12px] font-semibold">{a.title}</p>
                </div>
                <p className="text-[10px] leading-snug opacity-90">{a.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ───── CSV EXPORT FOOTER ───── */}
      {range.state.sessions.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-zinc-900">
          <p className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mr-auto">
            Export ·{' '}
            {range.state.sessions.filter((s) => s.richShots?.length).length} live sessions
          </p>
          <button
            onClick={exportSessionsCsv}
            className="text-[12px] text-zinc-400 hover:text-cyan-300 flex items-center gap-1.5 px-2 py-1.5 border border-zinc-800 rounded touch-manipulation"
            title="Download session-level CSV"
          >
            <Download size={11} /> Sessions.csv
          </button>
          <button
            onClick={exportShotsCsv}
            className="text-[12px] text-zinc-400 hover:text-cyan-300 flex items-center gap-1.5 px-2 py-1.5 border border-zinc-800 rounded touch-manipulation"
            title="Download per-shot CSV (live sessions only)"
          >
            <FileSpreadsheet size={11} /> Shots.csv
          </button>
        </div>
      )}
      </>
      )}

      {/* ───── LOG MODAL ───── */}
      {logOpen && (
        <LogSessionModal
          onClose={() => setLogOpen(false)}
          onSubmit={(s) => { range.logSession(s); setLogOpen(false); }}
        />
      )}

      {/* ───── BEGIN LIVE SESSION MODAL ───── */}
      {beginOpen && (
        <BeginSessionModal
          defaultSetupName={setups?.activeSetup?.name}
          onClose={() => setBeginOpen(false)}
          onBegin={(opts) => {
            activeSessionHook.begin({
              location: opts.location,
              conditions: {
                temperatureF: opts.temperatureF,
                altitudeFt: opts.altitudeFt,
                windMph: opts.windMph,
                windDirDeg: opts.windDirDeg,
              },
              setupSnapshot: snapshotSetup(),
              initialDistanceYd: opts.initialDistanceYd,
            });
            setBeginOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Cheap derivation of a v1 GroupPattern from v2 aggregates so the existing
 * diagnose engine still works on live sessions. Uses centroid bias:
 *   - tight + centered → 'centered'
 *   - centroid clearly left/right/high/low → directional pattern
 *   - high stddev with no bias → 'scattered'
 */
function derivePattern(
  aggs: ReturnType<typeof computeShotAggregates>,
): GP {
  if (!aggs) return 'centered';
  const { centerX, centerY, group95RadiusIn, meanRadiusIn } = aggs;
  if (meanRadiusIn < 0.6 && group95RadiusIn < 1.5) return 'centered';
  const ax = Math.abs(centerX);
  const ay = Math.abs(centerY);
  const bias = Math.max(ax, ay);
  const spread = group95RadiusIn;
  if (bias > 1.2 && bias > spread * 0.4) {
    if (ax > ay) return centerX < 0 ? 'left-string' : 'right-string';
    return centerY < 0 ? 'high' : 'low';
  }
  // Spread vs centeredness — vertical/horizontal stringing detection would
  // need axis-aware stddev; for v130 we settle for 'scattered' vs 'centered'.
  if (spread > 3) return 'scattered';
  return 'centered';
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, accent, icon,
}: { label: string; value: number; sub?: string; accent?: 'ember'; icon?: React.ReactNode }) {
  const valueColor = accent === 'ember' ? 'text-ember-500' : 'text-zinc-100';
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-md p-3">
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {icon}{label}
      </p>
      <p className={`text-2xl font-bold mt-0.5 ${valueColor}`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-500">{sub}</p>}
    </div>
  );
}

/**
 * Free-tier teaser shown in place of the full diagnose cards.
 *
 * Shows the diagnosis TITLE (so user sees their pattern was identified +
 * something useful exists) plus an upgrade CTA explaining what Hunter+
 * unlocks. Deliberately not punitive — leads with the value not the lock.
 */
function LockedDiagnoseTeaser({ recs }: { recs: ReturnType<typeof diagnose> }) {
  return (
    <Card padding="lg" className="border-zinc-700 bg-gradient-to-br from-zinc-900/80 to-zinc-950">
      <div className="flex items-baseline gap-2 mb-3">
        <Lock size={14} className="text-ember-500" />
        <p className="text-[11px] font-mono uppercase tracking-wider text-ember-500">
          {recs.length === 1 ? '1 fix identified' : `${recs.length} fixes identified`}
        </p>
      </div>

      {/* Show titles only — gives the user a peek at what was found */}
      <ul className="space-y-2 mb-4">
        {recs.map((rec, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-zinc-600 mt-0.5">›</span>
            <span className="text-sm text-zinc-200 font-medium leading-snug">
              {rec.title}
            </span>
          </li>
        ))}
      </ul>

      <div className="border-t border-zinc-800 pt-3 mt-2">
        <p className="text-[13px] text-zinc-300 leading-relaxed mb-3">
          Subscribe to <strong className="text-ember-500">Hunter</strong> or{' '}
          <strong className="text-sky-400">Competition</strong> ($6/mo, 7 days free)
          to unlock the specific WHY + FIX for each pattern, plus a one-click jump
          into the matching step of the Tuning Wizard.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => openUpgrade('hunter')}>
            Start 7-day Hunter trial
          </Button>
          <Button size="sm" variant="secondary" onClick={() => openUpgrade('hunter')}>
            See all tiers
          </Button>
        </div>
      </div>
    </Card>
  );
}

function DiagnoseCard({ rec }: { rec: ReturnType<typeof diagnose>[number] }) {
  const tone = {
    info:     'border-emerald-700/40 bg-emerald-950/20',
    warn:     'border-amber-700/40 bg-amber-950/20',
    critical: 'border-rose-700/40 bg-rose-950/20',
  }[rec.severity];

  return (
    <Card padding="lg" className={tone}>
      <p className="text-base font-semibold text-zinc-100 mb-2">{rec.title}</p>
      <p className="text-[13px] text-zinc-300 mb-3 leading-relaxed">
        <strong className="text-zinc-200">Why:</strong> {rec.why}
      </p>
      <p className="text-[13px] text-zinc-300 mb-3 leading-relaxed">
        <strong className="text-zinc-200">Fix:</strong> {rec.fix}
      </p>
      {rec.goToTuneStep && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            // Dispatch event the parent ArrowBuilder listens for to switch
            // to the Tune tab + jump to the right step.
            window.dispatchEvent(new CustomEvent('sparrow:goto-tune-step', {
              detail: { stepId: rec.goToTuneStep },
            }));
          }}
        >
          Open Tune step →
        </Button>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// v135 — Per-row session actions (share + delete)
// ─────────────────────────────────────────────────────────────────────────

function SessionRowActions({
  session,
  onDelete,
}: {
  session: ShotSession;
  onDelete: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 justify-end">
      <ShareButton
        getSnapshot={() =>
          buildSessionSnapshot(session, {
            customLabel: `${session.distanceYd}yd · ${session.arrowCount} arrows`,
          })
        }
        label=""
        className="text-zinc-500 hover:text-cyan-300 p-1"
      />
      <button
        onClick={onDelete}
        className="text-zinc-600 hover:text-rose-400 p-1"
        title="Delete session"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Log session modal
// ─────────────────────────────────────────────────────────────────────────

interface LogSessionModalProps {
  onClose: () => void;
  onSubmit: (s: Omit<ShotSession, 'id' | 'loggedAt'>) => void;
}

function LogSessionModal({ onClose, onSubmit }: LogSessionModalProps) {
  const [distance, setDistance] = useState<number>(40);
  const [arrowCount, setArrowCount] = useState<number>(6);
  const [groupSize, setGroupSize] = useState<number>(3);
  const [pattern, setPattern] = useState<GroupPattern>('centered');
  const [notes, setNotes] = useState<string>('');

  // Lock body scroll while modal is open (iOS Safari needs this to avoid
  // background scroll bleeding through). Escape closes.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const submit = () => {
    onSubmit({ distanceYd: distance, arrowCount, groupSizeIn: groupSize, pattern, notes: notes || undefined });
  };

  /*
    Portal to body — without this the modal gets trapped by a transformed
    ancestor (sticky header uses backdrop-blur which creates a containing
    block for position:fixed) and the bottom of the modal falls off-screen
    on phones. Same pattern as CheatSheetPanel and TierPickerModal.

    Layout: outer flex column = modal panel. Header fixed at top, body
    flex-1 + overflow-y-auto = scrollable middle, footer fixed at bottom.
    Total height capped at 100dvh so the footer is ALWAYS visible no
    matter how long the body content is.
  */
  const node = (
    <div
      className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm"
      style={{ height: '100dvh' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg bg-zinc-950 border-t sm:border border-zinc-800 sm:rounded-lg shadow-2xl flex flex-col"
        style={{ maxHeight: '100dvh', height: '100dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex-shrink-0 px-5 py-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-950">
          <h2 className="text-lg font-bold">Log a session</h2>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-100 p-2 -m-2 text-2xl leading-none touch-manipulation"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* v133.2 — shared DistancePicker (presets + More + Custom… + Recent). */}
          <div>
            <label className="text-[11px] text-zinc-500 block mb-1.5 font-mono uppercase tracking-wider">
              Distance (yards)
            </label>
            <DistancePicker value={distance} onChange={setDistance} />
          </div>

          {/* Arrow count + group size */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-500 block mb-1.5 font-mono uppercase tracking-wider">
                Arrows in group
              </label>
              <input
                type="number"
                value={arrowCount}
                onChange={(e) => setArrowCount(parseInt(e.target.value, 10) || 0)}
                min={1}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500 block mb-1.5 font-mono uppercase tracking-wider">
                Group size (inches)
              </label>
              <input
                type="number"
                value={groupSize}
                onChange={(e) => setGroupSize(parseFloat(e.target.value) || 0)}
                step={0.1}
                min={0}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
                placeholder="outer to outer"
              />
            </div>
          </div>

          {/* Pattern selector */}
          <div>
            <label className="text-[11px] text-zinc-500 block mb-1.5 font-mono uppercase tracking-wider">
              What did the group look like?
            </label>
            <div className="space-y-1.5">
              {(Object.keys(GROUP_PATTERN_LABELS) as GroupPattern[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPattern(p)}
                  className={
                    'w-full text-left px-3 py-2 rounded text-sm border transition ' +
                    (pattern === p
                      ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
                      : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
                  }
                >
                  {GROUP_PATTERN_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[11px] text-zinc-500 block mb-1.5 font-mono uppercase tracking-wider">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm resize-none"
              placeholder="e.g. windy, new strings, tried thumb release..."
            />
          </div>
        </div>

        <footer className="flex-shrink-0 px-5 py-4 border-t border-zinc-800 flex justify-end gap-2 bg-zinc-950">
          <Button variant="secondary" size="md" onClick={onClose}>Cancel</Button>
          <Button size="md" leftIcon={<TrendingDown size={14} />} onClick={submit}>
            Log session
          </Button>
        </footer>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

// ─────────────────────────────────────────────────────────────────────────
// Date formatters
// ─────────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─────────────────────────────────────────────────────────────────────────
// Begin Live Session modal
// ─────────────────────────────────────────────────────────────────────────

interface BeginSessionModalProps {
  onClose: () => void;
  onBegin: (opts: {
    location?: string;
    initialDistanceYd?: number;
    temperatureF?: number;
    altitudeFt?: number;
    windMph?: number;
    windDirDeg?: number;
  }) => void;
  defaultSetupName?: string;
}

function BeginSessionModal({ onClose, onBegin, defaultSetupName }: BeginSessionModalProps) {
  const [location, setLocation] = useState('');
  const [distance, setDistance] = useState(40);
  const [temp, setTemp] = useState('');
  const [alt, setAlt] = useState('');
  const [wind, setWind] = useState('');
  const [windDir, setWindDir] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const submit = () => {
    onBegin({
      location: location || undefined,
      initialDistanceYd: distance,
      temperatureF: temp ? parseFloat(temp) : undefined,
      altitudeFt: alt ? parseFloat(alt) : undefined,
      windMph: wind ? parseFloat(wind) : undefined,
      windDirDeg: windDir ? parseFloat(windDir) : undefined,
    });
  };

  const node = (
    <div
      className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm"
      style={{ height: '100dvh' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg bg-zinc-950 border-t sm:border border-zinc-800 sm:rounded-lg shadow-2xl flex flex-col"
        style={{ maxHeight: '100dvh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex-shrink-0 px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Start live session</h2>
            <p className="text-[11px] text-zinc-500">
              {defaultSetupName ? `Bow + arrow snapshot: ${defaultSetupName}` : 'No bow snapshot — set up a Setup first to capture it.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-100 p-2 -m-2 text-2xl leading-none touch-manipulation"
            aria-label="Close"
          >×</button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          <label className="block">
            <span className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider block mb-1">Range / location (optional)</span>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
              placeholder="Backyard, 3D course, club indoor..."
            />
          </label>

          <div>
            <p className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider mb-1">Starting distance</p>
            {/* v133.2 — shared DistancePicker: presets + More + Custom… + Recent. */}
            <DistancePicker value={distance} onChange={setDistance} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider block mb-1">Temp (°F)</span>
              <input type="number" value={temp} onChange={(e) => setTemp(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" placeholder="optional" />
            </label>
            <label className="block">
              <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider block mb-1">Altitude (ft)</span>
              <input type="number" value={alt} onChange={(e) => setAlt(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" placeholder="optional" />
            </label>
            <label className="block">
              <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider block mb-1">Wind (mph)</span>
              <input type="number" step={0.5} value={wind} onChange={(e) => setWind(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" placeholder="optional" />
            </label>
            <label className="block">
              <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider block mb-1">Wind dir (°)</span>
              <input type="number" value={windDir} onChange={(e) => setWindDir(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" placeholder="0=N 90=E" />
            </label>
          </div>

          <p className="text-[11px] text-zinc-500 border-t border-zinc-900 pt-3">
            All conditions are optional. Sparrow will roll session-level wind down to each shot unless you override it per-shot. Everything is local to your device until server sync ships.
          </p>
        </div>

        <footer className="flex-shrink-0 px-5 py-4 border-t border-zinc-800 flex justify-end gap-2 bg-zinc-950">
          <Button variant="secondary" size="md" onClick={onClose}>Cancel</Button>
          <Button size="md" leftIcon={<Activity size={14} />} onClick={submit}>Start session</Button>
        </footer>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(node, document.body);
}

function formatRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  return formatDate(iso);
}
